import React from 'react';
import { Drawer } from '../../design-system';
import { SwHeightDefaults } from '../../@types/swHeight/types';
import { SwHeightPatchApi } from './useSwHeightSettingsPatch';
import {
  METHOD_HINT,
  SECTION_TITLES,
  SwHeightSection
} from './swHeightSettings';
import SwHeightCutoffsPanel from './SwHeightCutoffsPanel';
import SwHeightParametersPanel from './SwHeightParametersPanel';

export interface SwHeightSectionDrawersProps {
  openSection: SwHeightSection | null;
  onClose: () => void;
  api: SwHeightPatchApi;
  defaults: SwHeightDefaults | null;
  disabled?: boolean;
}

/**
 * The two parameter groups that are *columns of fields*, so a narrow side
 * sheet suits them.
 *
 * The third group, the per-rock-type table, is not here: a table needs width,
 * and squeezing seven columns into 480px collapsed them on top of each other.
 * It lives in a full-width dock under the charts instead — see
 * `SwHeightRockTypeDock`.
 *
 * Drawers rather than modals, and deliberately non-blocking: every value in
 * here changes a curve, so the curve has to stay visible — and clickable —
 * while it is being changed.
 *
 * Each drawer's title is the section's own heading, which is why the bodies
 * render theirs only when standalone (`showHeader={false}` here): two copies
 * of the same string would be one too many both visually and for any test
 * that looks the heading up by text.
 *
 * The subtitle echoes the active method. A right-anchored drawer covers the
 * rail, so the Method selector — which decides what these groups contain —
 * is hidden exactly when it is most relevant.
 */
export const SwHeightSectionDrawers: React.FC<SwHeightSectionDrawersProps> = ({
  openSection,
  onClose,
  api,
  defaults,
  disabled = false
}) => {
  const subtitle = METHOD_HINT[api.method];

  return (
    <>
      <Drawer
        open={openSection === 'cutoffs'}
        onClose={onClose}
        title={SECTION_TITLES.cutoffs}
        subtitle={subtitle}
        size="sm"
        data-testid="sw-height-drawer-cutoffs"
      >
        <SwHeightCutoffsPanel
          api={api}
          showHeader={false}
          disabled={disabled}
        />
      </Drawer>

      <Drawer
        open={openSection === 'parameters'}
        onClose={onClose}
        title={SECTION_TITLES.parameters}
        subtitle={subtitle}
        size="sm"
        data-testid="sw-height-drawer-parameters"
      >
        <SwHeightParametersPanel
          api={api}
          defaults={defaults}
          showHeader={false}
          disabled={disabled}
        />
      </Drawer>

    </>
  );
};

export default SwHeightSectionDrawers;
